import { RefObject, useEffect, useMemo, useState } from 'react';
import {
  CalendarWeek,
  MonthBoundary,
  getMonthYearLabel,
  isFirstOfMonth,
} from './calendar-utils';

type SentinelObservation = {
  isIntersecting: boolean;
  boundingTop: number;
  rootTop: number;
};

export function isSentinelPassed(observation: SentinelObservation): boolean {
  if (observation.isIntersecting) return false;
  return observation.boundingTop < observation.rootTop;
}

export function computeActiveDate(
  initialDate: string,
  passedDates: ReadonlySet<string>,
): string {
  if (passedDates.size === 0) return initialDate;
  const sorted = [...passedDates].sort();
  return sorted[sorted.length - 1] ?? initialDate;
}

export function getInitialMonthDate(weeks: CalendarWeek[]): string | null {
  const firstWeek = weeks[0];
  if (!firstWeek) return null;
  const firstOfMonth = firstWeek.days.find((day) => isFirstOfMonth(day.date));
  return firstOfMonth?.date ?? firstWeek.days[0]?.date ?? null;
}

type UseScrollAwareMonthHeaderArgs = {
  weeks: CalendarWeek[];
  boundaries: MonthBoundary[];
  locale: string;
  sentinelRefs: RefObject<Map<string, HTMLElement | null>>;
  scrollRoot: HTMLElement | null;
};

// The header sits OUTSIDE the scroll container as a structural sibling, so
// the grid's clientRect top edge already lines up with the header's bottom
// edge — no rootMargin offset is needed. Using the grid as the IO root means
// scroll inside the grid drives the intersection callbacks.
export function useScrollAwareMonthHeader({
  weeks,
  boundaries,
  locale,
  sentinelRefs,
  scrollRoot,
}: UseScrollAwareMonthHeaderArgs): string {
  const initialDate = useMemo(() => getInitialMonthDate(weeks), [weeks]);
  const [activeDate, setActiveDate] = useState<string | null>(initialDate);

  useEffect(() => {
    setActiveDate(initialDate);
  }, [initialDate]);

  useEffect(() => {
    if (!initialDate || boundaries.length === 0) return;
    if (typeof IntersectionObserver === 'undefined') return;
    // Wait until the scroll container has mounted; otherwise the observer
    // would silently fall back to viewport intersection.
    if (!scrollRoot) return;

    const passedDates = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const isoDate = (entry.target as HTMLElement).dataset['monthYear'];
          if (!isoDate) continue;
          const passed = isSentinelPassed({
            isIntersecting: entry.isIntersecting,
            boundingTop: entry.boundingClientRect.top,
            rootTop: entry.rootBounds?.top ?? 0,
          });
          if (passed) {
            passedDates.add(isoDate);
          } else {
            passedDates.delete(isoDate);
          }
        }
        setActiveDate(computeActiveDate(initialDate, passedDates));
      },
      {
        root: scrollRoot,
        threshold: 0,
      },
    );

    const sentinels = sentinelRefs.current;
    if (!sentinels) return;

    for (const boundary of boundaries) {
      const el = sentinels.get(boundary.isoDate);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [boundaries, initialDate, scrollRoot, sentinelRefs]);

  return activeDate ? getMonthYearLabel(activeDate, locale) : '';
}
